/**
 * OrderDetailPage.js - 订单详情与管理页
 * 展示完整订单信息，支持修改状态、保存备注、取消订单、退款等操作
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Row,
  Col,
  Descriptions,
  Table,
  Tag,
  Button,
  Select,
  Input,
  Space,
  Divider,
  Popconfirm,
  message,
  Spin,
  Typography,
  Statistic,
  Modal,
  Form,
  Grid,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  StopOutlined,
  DeleteOutlined,
  DownOutlined,
  DollarOutlined,
  CreditCardOutlined,
  WalletOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { adminOrderApi } from '../../api/index';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { useBreakpoint } = Grid;

// 订单状态配置
const statusConfig = {
  pending: { color: 'orange', label: '待支付' },
  paid: { color: 'green', label: '已支付' },
  shipped: { color: 'blue', label: '已发货' },
  completed: { color: 'purple', label: '已完成' },
  cancelled: { color: 'red', label: '已取消' },
};

// 财务状态标签
const financialStatusLabel = {
  pending: '待支付',
  paid: '已支付',
  refunded: '已退款',
  partially_refunded: '部分退款',
  voided: '已作废',
};

// 支付状态标签
const paymentStatusLabel = {
  pending: { color: 'orange', label: '待支付' },
  success: { color: 'green', label: '支付成功' },
  failure: { color: 'red', label: '支付失败' },
  closed: { color: 'default', label: '已关闭' },
};

export default function OrderDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [shippingModalVisible, setShippingModalVisible] = useState(false);
  const [refundModalVisible, setRefundModalVisible] = useState(false);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);
  const [refunds, setRefunds] = useState([]);
  const [refundRecordsLoading, setRefundRecordsLoading] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [refundOtp, setRefundOtp] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  // 编辑商品弹窗
  const [editItemsModalVisible, setEditItemsModalVisible] = useState(false);
  const [editingItems, setEditingItems] = useState([]);
  const [editReason, setEditReason] = useState('');
  const [editingItemsLoading, setEditingItemsLoading] = useState(false);
  // 编辑收货地址弹窗
  const [editAddressModalVisible, setEditAddressModalVisible] = useState(false);
  const [addressForm] = Form.useForm();
  const [addressSaving, setAddressSaving] = useState(false);

  // 当前选择的新状态
  const [selectedStatus, setSelectedStatus] = useState('');
  // 管理员备注
  const [adminNote, setAdminNote] = useState('');
  // 发货表单
  const [shippingForm] = Form.useForm();

  // 拉取订单详情
  const fetchOrder = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminOrderApi.getById(id);
      setOrder(data);
      setSelectedStatus(data.status || '');
      setAdminNote(data.adminNote || '');
    } catch (err) {
      message.error('加载订单失败：' + (err.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  // 加载退款记录
  const fetchRefunds = useCallback(async () => {
    if (!id) return;
    setRefundRecordsLoading(true);
    try {
      const data = await adminOrderApi.getRefunds(id);
      setRefunds(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('加载退款记录失败:', err);
    } finally {
      setRefundRecordsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  useEffect(() => {
    if (order?.id) {
      fetchRefunds();
    }
  }, [order?.id, fetchRefunds]);

  // OTP 60 秒倒计时
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const t = setTimeout(() => setOtpCooldown(otpCooldown - 1), 1000);
    return () => clearTimeout(t);
  }, [otpCooldown]);

  // 更新订单状态
  const handleStatusUpdate = async () => {
    if (!selectedStatus || selectedStatus === order?.status) {
      message.info('状态未变更');
      return;
    }
    setStatusUpdating(true);
    try {
      await adminOrderApi.updateStatus(id, selectedStatus);
      message.success('订单状态已更新');
      fetchOrder();
    } catch (err) {
      message.error('状态更新失败：' + (err.message || '未知错误'));
    } finally {
      setStatusUpdating(false);
    }
  };

  // 保存管理员备注
  const handleNoteSave = async () => {
    setNoteSaving(true);
    try {
      await adminOrderApi.update(id, { adminNote });
      message.success('备注已保存');
    } catch (err) {
      message.error('备注保存失败：' + (err.message || '未知错误'));
    } finally {
      setNoteSaving(false);
    }
  };

  // 取消订单
  const handleCancel = async () => {
    setCancelling(true);
    try {
      await adminOrderApi.cancel(id);
      message.success('订单已取消');
      fetchOrder();
    } catch (err) {
      message.error('取消订单失败：' + (err.message || '未知错误'));
    } finally {
      setCancelling(false);
    }
  };

  // 删除订单
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await adminOrderApi.delete(id);
      message.success('订单已删除');
      navigate('/admin/orders');
    } catch (err) {
      message.error('删除订单失败：' + (err.message || '未知错误'));
    } finally {
      setDeleting(false);
    }
  };

  // 打开发货弹窗
  const handleShip = () => {
    shippingForm.setFieldsValue({
      trackingNumber: order?.trackingNumber || '',
      trackingCarrier: order?.trackingCarrier || '',
    });
    setShippingModalVisible(true);
  };

  // 确认发货
  const handleShipConfirm = async () => {
    try {
      const values = await shippingForm.validateFields();
      await adminOrderApi.ship(id, values);
      message.success('订单已发货');
      setShippingModalVisible(false);
      fetchOrder();
    } catch (err) {
      message.error('发货失败：' + (err.message || '未知错误'));
    }
  };

  // 打开退款弹窗
  const handleRefundOpen = (payment) => {
    // 计算该支付记录的可退款金额
    const paymentRefunds = refunds.filter(r => r.paymentId === payment.id);
    const alreadyRefunded = paymentRefunds.reduce((sum, r) => sum + (r.amount || 0), 0);
    const maxRefund = (payment.amount || 0) - alreadyRefunded;
    setSelectedPaymentId(payment.id);
    setRefundAmount(maxRefund.toFixed(2));
    setRefundReason('');
    setRefundOtp('');
    setOtpCooldown(0);
    setRefundModalVisible(true);
  };

  // 发送退款 OTP
  const handleSendOtp = async () => {
    setOtpSending(true);
    try {
      await adminOrderApi.sendRefundOtp(order.id);
      message.success('验证码已发送至管理员邮箱');
      setOtpCooldown(60);
    } catch (err) {
      message.error('发送验证码失败：' + (err.message || '未知错误'));
    } finally {
      setOtpSending(false);
    }
  };

  // 确认退款
  const handleRefundConfirm = async () => {
    if (!refundAmount || Number(refundAmount) <= 0) {
      message.error('请输入有效的退款金额');
      return;
    }
    if (!refundOtp || refundOtp.length !== 6) {
      message.error('请输入 6 位验证码');
      return;
    }
    setRefunding(true);
    try {
      await adminOrderApi.refund(order.id, selectedPaymentId, Number(refundAmount), refundReason, refundOtp);
      message.success('退款已提交');
      setRefundModalVisible(false);
      // 同时刷新订单和退款记录
      await fetchOrder();
      await fetchRefunds();
    } catch (err) {
      message.error('退款失败：' + (err.message || '未知错误'));
    } finally {
      setRefunding(false);
    }
  };

  // 打开编辑商品弹窗
  const handleEditItemsOpen = () => {
    const currentItems = items.map(item => ({
      orderItemId: item.id,
      title: item.title || item.productTitle,
      variantTitle: item.variantTitle,
      sku: item.sku,
      price: item.price,
      currentQuantity: item.quantity,
      newQuantity: item.quantity,
    }));
    setEditingItems(currentItems);
    setEditReason('');
    setEditItemsModalVisible(true);
  };

  // 更新编辑商品数量
  const handleEditItemChange = (orderItemId, newQuantity) => {
    setEditingItems(prev =>
      prev.map(item =>
        item.orderItemId === orderItemId
          ? { ...item, newQuantity: Math.max(0, Number(newQuantity) || 0) }
          : item
      )
    );
  };

  // 确认编辑商品
  const handleEditItemsConfirm = async () => {
    // 过滤出有变化的商品
    const changedItems = editingItems.filter(
      item => item.newQuantity !== item.currentQuantity
    );

    if (changedItems.length === 0) {
      message.info('商品数量未变更');
      return;
    }

    // 验证数量不能全为0（至少保留一个商品）
    const totalNewQty = editingItems.reduce((sum, item) => sum + item.newQuantity, 0);
    if (totalNewQty === 0) {
      message.error('订单至少需要保留一个商品');
      return;
    }

    setEditingItemsLoading(true);
    try {
      const payload = {
        items: changedItems.map(item => ({
          orderItemId: item.orderItemId,
          newQuantity: item.newQuantity,
        })),
        reason: editReason || '管理员调整订单商品数量',
      };
      await adminOrderApi.editItems(order.id, payload);
      message.success('订单商品已更新');
      setEditItemsModalVisible(false);
      // 刷新订单和退款记录
      await fetchOrder();
      await fetchRefunds();
    } catch (err) {
      message.error('编辑失败：' + (err.message || '未知错误'));
    } finally {
      setEditingItemsLoading(false);
    }
  };

  // 打开编辑收货地址弹窗
  const handleEditAddressOpen = () => {
    addressForm.setFieldsValue({
      shippingFirstName: order?.shippingFirstName || '',
      shippingLastName: order?.shippingLastName || '',
      shippingPhone: order?.shippingPhone || '',
      shippingAddress1: order?.shippingAddress1 || '',
      shippingAddress2: order?.shippingAddress2 || '',
      shippingCity: order?.shippingCity || '',
      shippingProvince: order?.shippingProvince || '',
      shippingPostalCode: order?.shippingPostalCode || '',
      shippingCountry: order?.shippingCountry || 'US',
    });
    setEditAddressModalVisible(true);
  };

  // 确认编辑收货地址
  const handleEditAddressConfirm = async () => {
    try {
      const values = await addressForm.validateFields();
      setAddressSaving(true);
      await adminOrderApi.update(order.id, {
        shippingFirstName: values.shippingFirstName,
        shippingLastName: values.shippingLastName,
        shippingPhone: values.shippingPhone,
        shippingAddress1: values.shippingAddress1,
        shippingAddress2: values.shippingAddress2 || '',
        shippingCity: values.shippingCity,
        shippingProvince: values.shippingProvince || '',
        shippingPostalCode: values.shippingPostalCode,
        shippingCountry: values.shippingCountry || 'US',
      });
      message.success('收货地址已更新');
      setEditAddressModalVisible(false);
      await fetchOrder();
    } catch (err) {
      if (err.errorFields) {
        // 表单验证错误，不需要显示 message
        return;
      }
      message.error('更新失败：' + (err.message || '未知错误'));
    } finally {
      setAddressSaving(false);
    }
  };

  // 订单商品表格列
  const itemColumns = [
    {
      title: '商品',
      dataIndex: 'productTitle',
      key: 'productTitle',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{text || record.title || '-'}</div>
          {record.variantTitle && (
            <div style={{ fontSize: 12, color: '#888' }}>{record.variantTitle}</div>
          )}
        </div>
      ),
    },
    {
      title: 'SKU',
      dataIndex: 'sku',
      key: 'sku',
      width: isMobile ? 80 : 130,
      render: (sku) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#666' }}>
          {sku || '-'}
        </span>
      ),
    },
    {
      title: '单价',
      dataIndex: 'price',
      key: 'price',
      width: 80,
      align: 'right',
      render: (val) => `$${Number(val || 0).toFixed(2)}`,
    },
    {
      title: '数量',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 60,
      align: 'center',
    },
    {
      title: '小计',
      key: 'lineTotal',
      width: 90,
      align: 'right',
      render: (_, record) =>
        `$${(Number(record.price || 0) * Number(record.quantity || 1)).toFixed(2)}`,
    },
  ];

  // 退款记录表格列
  const refundColumns = [
    {
      title: '退款时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 140,
      render: (time) => (
        <span style={{ fontSize: 12, color: '#666' }}>
          {time ? new Date(time).toLocaleString('zh-CN', { hour12: false }) : '-'}
        </span>
      ),
    },
    {
      title: '退款金额',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      align: 'right',
      render: (val) => (
        <Text strong style={{ color: '#c44536' }}>
          ${Number(val || 0).toFixed(2)}
        </Text>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => (
        <Tag color={status === 'success' ? 'green' : 'orange'}>
          {status === 'success' ? '成功' : '处理中'}
        </Tag>
      ),
    },
    {
      title: '退款原因',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      render: (text) => text || '-',
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!order) {
    return (
      <div style={{ padding: 40 }}>
        <Text type="danger">订单不存在或加载失败</Text>
        <Button
          style={{ marginLeft: 16 }}
          onClick={() => navigate('/admin/orders')}
        >
          返回列表
        </Button>
      </div>
    );
  }

  const cfg = statusConfig[order.status] || { color: 'default', label: order.status };
  // 收货地址：从订单字段直接获取
  const shippingAddress = {
    firstName: order.shippingFirstName,
    lastName: order.shippingLastName,
    address1: order.shippingAddress1,
    address2: order.shippingAddress2,
    city: order.shippingCity,
    province: order.shippingProvince,
    zip: order.shippingPostalCode,
    country: order.shippingCountry,
    phone: order.shippingPhone,
  };
  const items = order.items || order.lineItems || [];
  const payments = order.payments || [];

  // 价格汇总
  const subtotal = Number(order.subtotal || order.itemsTotal || 0);
  const shipping = Number(order.shippingFee || order.shippingTotal || 0);
  const discount = Number(order.discountAmount || order.discountTotal || 0);
  const total = Number(order.total || 0);

  // 获取退款弹窗中的支付记录
  const getRefundModalPayment = () => payments.find(p => p.id === selectedPaymentId);

  return (
    <div>
      {/* 顶部标题栏 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 24,
          flexWrap: 'wrap',
        }}
      >
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/admin/orders')}
        >
          返回列表
        </Button>
        <Title level={4} style={{ margin: 0 }}>
          订单详情 #{order.orderNo}
        </Title>
        <Tag color={cfg.color} style={{ fontSize: 13, padding: '2px 10px' }}>
          {cfg.label}
        </Tag>
      </div>

      <Row gutter={[16, 16]}>
        {/* 左侧：主要信息 */}
        <Col xs={24} lg={16}>
          {/* 基本信息 */}
          <Card title="订单信息" style={{ borderRadius: 8, marginBottom: 16 }}>
            <Descriptions column={{ xs: 1, sm: 2 }} size="small">
              <Descriptions.Item label="订单号">
                <span style={{ fontFamily: 'monospace' }}>#{order.orderNo}</span>
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {order.createdAt
                  ? new Date(order.createdAt).toLocaleString('zh-CN', { hour12: false })
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="客户邮箱">
                  {order.email && order.userId ? (
                    <a onClick={() => navigate(`/admin/customers/${order.userId}`)} style={{ color: '#1677ff' }}>
                      {order.email}
                    </a>
                  ) : (
                    <span>{order.email || order.customer?.email || '-'}</span>
                  )}
                </Descriptions.Item>
              <Descriptions.Item label="财务状态">
                {financialStatusLabel[order.financialStatus] || order.financialStatus || '-'}
              </Descriptions.Item>
              {order.trackingNumber && (
                <Descriptions.Item label="物流单号" span={2}>
                  <span style={{ fontFamily: 'monospace' }}>{order.trackingNumber}</span>
                  {order.trackingCarrier && <span style={{ marginLeft: 16, color: '#888' }}>{order.trackingCarrier}</span>}
                </Descriptions.Item>
              )}
              {order.note && (
                <Descriptions.Item label="客户备注" span={2}>
                  {order.note}
                </Descriptions.Item>
              )}
              {order.discountCode && (
                <Descriptions.Item label="优惠码">
                  <Tag>{order.discountCode}</Tag>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* 订单商品明细 */}
          <Card
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>商品明细</span>
                {(order.status === 'paid' || order.status === 'shipped' || order.status === 'processing') && (
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={handleEditItemsOpen}
                    style={{ padding: 0 }}
                  >
                    编辑商品
                  </Button>
                )}
              </div>
            }
            style={{ borderRadius: 8, marginBottom: 16 }}
            bodyStyle={{ padding: 0 }}
          >
            <Table
              dataSource={items}
              columns={itemColumns}
              rowKey={(r, i) => r.id || i}
              pagination={false}
              locale={{ emptyText: '暂无商品' }}
              scroll={{ x: 400 }}
              size="small"
            />

            {/* 价格汇总 */}
            <div
              style={{
                padding: '12px 16px',
                borderTop: '1px solid #f0f0f0',
                maxWidth: 280,
                marginLeft: 'auto',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <Text type="secondary">商品小计</Text>
                <Text>${subtotal.toFixed(2)}</Text>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <Text type="secondary">运费</Text>
                <Text>${shipping.toFixed(2)}</Text>
              </div>
              {discount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <Text type="secondary">优惠折扣</Text>
                  <Text type="success">-${discount.toFixed(2)}</Text>
                </div>
              )}
              <Divider style={{ margin: '6px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text strong style={{ fontSize: 14 }}>总计</Text>
                <Text strong style={{ fontSize: 14 }}>${total.toFixed(2)}</Text>
              </div>
            </div>
          </Card>
        </Col>

        {/* 右侧：操作区域 */}
        <Col xs={24} lg={8}>
          {/* 状态更新 */}
          <Card title="更新状态" style={{ borderRadius: 8, marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Select
                value={selectedStatus}
                onChange={setSelectedStatus}
                style={{ width: '100%' }}
              >
                <Option value="pending">待支付</Option>
                <Option value="paid">已支付</Option>
                <Option value="shipped">已发货</Option>
                <Option value="completed">已完成</Option>
                <Option value="cancelled">已取消</Option>
              </Select>
              <Button
                type="primary"
                block
                loading={statusUpdating}
                onClick={handleStatusUpdate}
                disabled={!selectedStatus || selectedStatus === order.status}
              >
                更新状态
              </Button>
            </Space>
            {order.status !== 'cancelled' && order.status !== 'completed' && order.status !== 'shipped' && (
              <Button
                style={{ marginTop: 12 }}
                type="primary"
                block
                icon={<DownOutlined />}
                onClick={handleShip}
              >
                订单发货
              </Button>
            )}
          </Card>

          {/* 收货地址 */}
          <Card
            title="收货地址"
            style={{ borderRadius: 8, marginBottom: 16 }}
            extra={
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={handleEditAddressOpen}
                style={{ padding: 0 }}
              >
                编辑
              </Button>
            }
          >
            {(shippingAddress.firstName || shippingAddress.lastName) ? (
              <div style={{ lineHeight: 1.8, fontSize: 14 }}>
                <div>
                  <strong>
                    {shippingAddress.lastName} {shippingAddress.firstName}
                  </strong>
                </div>
                {shippingAddress.phone && <div>{shippingAddress.phone}</div>}
                {shippingAddress.address1 && <div>{shippingAddress.address1}</div>}
                {shippingAddress.address2 && <div>{shippingAddress.address2}</div>}
                <div>
                  {[
                    shippingAddress.city,
                    shippingAddress.province,
                    shippingAddress.zip,
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </div>
                {shippingAddress.country && <div>{shippingAddress.country}</div>}
              </div>
            ) : (
              <Text type="secondary">暂无收货地址</Text>
            )}
          </Card>

          {/* 发货详情 */}
          <Card title="发货详情" style={{ borderRadius: 8, marginBottom: 16 }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="发货状态">
                {order.fulfillmentStatus ? (
                  <Tag color={order.fulfillmentStatus === 'shipped' ? 'blue' : 'default'}>
                    {order.fulfillmentStatus}
                  </Tag>
                ) : (
                  <span style={{ color: '#999' }}>未发货</span>
                )}
              </Descriptions.Item>
              {order.trackingNumber && (
                <>
                  <Descriptions.Item label="物流单号">
                    <span style={{ fontFamily: 'monospace' }}>{order.trackingNumber}</span>
                  </Descriptions.Item>
                  <Descriptions.Item label="物流公司">
                    {order.trackingCarrier || '未指定'}
                  </Descriptions.Item>
                </>
              )}
              {order.shippedAt && (
                <Descriptions.Item label="发货时间">
                  {new Date(order.shippedAt).toLocaleString('zh-CN', { hour12: false })}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          {/* 支付详情 - 重新排版 */}
          <Card
            title={<Space><CreditCardOutlined /> 支付详情</Space>}
            style={{ borderRadius: 8, marginBottom: 16 }}
          >
            {payments.length > 0 ? (
              payments.map((payment, i) => {
                // 计算该支付记录的退款情况
                const paymentRefunds = refunds.filter(r => r.paymentId === payment.id);
                const alreadyRefunded = paymentRefunds.reduce((sum, r) => sum + (r.amount || 0), 0);
                const refundableAmount = (payment.amount || 0) - alreadyRefunded;
                const isFullyRefunded = refundableAmount <= 0.01;
                const canRefund = payment.status === 'success' && refundableAmount > 0.01;
                const paymentStatus = paymentStatusLabel[payment.status] || { color: 'default', label: payment.status };

                return (
                  <div
                    key={payment.id || i}
                    style={{
                      padding: 16,
                      marginBottom: i < payments.length - 1 ? 8 : 0,
                      background: '#fafafa',
                      borderRadius: 8,
                    }}
                  >
                    {/* 支付基本信息 */}
                    <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                      <Col>
                        <Space size={4}>
                          <Text strong style={{ fontSize: 15 }}>{payment.vendor || '在线支付'}</Text>
                          <Tag color={paymentStatus.color}>{paymentStatus.label}</Tag>
                        </Space>
                      </Col>
                      <Col>
                        <Text strong style={{ fontSize: 18, color: '#52c41a' }}>
                          ${Number(payment.amount || 0).toFixed(2)}
                        </Text>
                      </Col>
                    </Row>

                    {/* 支付详情 */}
                    <div style={{ marginBottom: 12 }}>
                      <Row style={{ marginBottom: 4 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>交易流水号</Text>
                      </Row>
                      <Text style={{ fontFamily: 'monospace', fontSize: 12, color: '#666' }}>
                        {payment.transactionId || '-'}
                      </Text>
                    </div>

                    {/* 参考号 */}
                    <div style={{ marginBottom: 12 }}>
                      <Row style={{ marginBottom: 4 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>参考号</Text>
                      </Row>
                      <Text style={{ fontFamily: 'monospace', fontSize: 12, color: '#666' }}>
                        {payment.reference || '-'}
                      </Text>
                    </div>

                    <Divider style={{ margin: '8px 0' }} />

                    {/* 退款情况 */}
                    <Row justify="space-between" style={{ marginBottom: 8 }}>
                      <Col>
                        <Space size={4}>
                          <DollarOutlined style={{ color: '#c44536' }} />
                          <Text style={{ fontSize: 13 }}>已退款</Text>
                        </Space>
                      </Col>
                      <Col>
                        <Text strong style={{ color: alreadyRefunded > 0 ? '#c44536' : '#999', fontSize: 14 }}>
                          ${alreadyRefunded.toFixed(2)}
                        </Text>
                      </Col>
                    </Row>

                    <Row justify="space-between" align="middle">
                      <Col>
                        <Space size={4}>
                          <WalletOutlined style={{ color: refundableAmount > 0 ? '#52c41a' : '#999' }} />
                          <Text style={{ fontSize: 13 }}>可退款</Text>
                        </Space>
                      </Col>
                      <Col>
                        <Space size={8}>
                          <Text strong style={{ color: refundableAmount > 0 ? '#52c41a' : '#999', fontSize: 14 }}>
                            ${refundableAmount.toFixed(2)}
                          </Text>
                          {canRefund && (
                            <Button
                              size="small"
                              type="primary"
                              onClick={() => handleRefundOpen(payment)}
                              style={{ borderRadius: 4 }}
                            >
                              退款
                            </Button>
                          )}
                          {isFullyRefunded && (
                            <Tag color="default" style={{ fontSize: 11 }}>已全额退款</Tag>
                          )}
                        </Space>
                      </Col>
                    </Row>
                  </div>
                );
              })
            ) : (
              <Text type="secondary">暂无支付记录</Text>
            )}
          </Card>

          {/* 退款记录 */}
          <Card
            title={<Space><DollarOutlined /> 退款记录 ({refunds.length})</Space>}
            style={{ borderRadius: 8, marginBottom: 16 }}
          >
            {refundRecordsLoading ? (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <Spin size="small" />
              </div>
            ) : refunds.length > 0 ? (
              <Table
                dataSource={refunds}
                columns={refundColumns}
                rowKey="id"
                pagination={false}
                size="small"
                locale={{ emptyText: '暂无退款记录' }}
                scroll={{ x: 400 }}
              />
            ) : (
              <Text type="secondary">暂无退款记录</Text>
            )}
          </Card>

          {/* 管理员备注 */}
          <Card title="管理员备注" style={{ borderRadius: 8, marginBottom: 16 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <TextArea
                rows={3}
                placeholder="输入内部备注（客户不可见）"
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
              />
              <Button
                icon={<SaveOutlined />}
                loading={noteSaving}
                onClick={handleNoteSave}
                block
              >
                保存备注
              </Button>
            </Space>
          </Card>

          {/* 危险操作：取消订单 */}
          {order.status !== 'cancelled' && order.status !== 'completed' && (
            <Card
              style={{
                borderRadius: 8,
                borderColor: '#ff4d4f',
                background: '#fff5f5',
              }}
            >
              <Popconfirm
                title="确认取消该订单？"
                description="取消后无法恢复，请谨慎操作。"
                onConfirm={handleCancel}
                okText="确认取消"
                cancelText="取消"
                okButtonProps={{ danger: true }}
              >
                <Button
                  danger
                  block
                  icon={<StopOutlined />}
                  loading={cancelling}
                >
                  取消订单
                </Button>
              </Popconfirm>
            </Card>
          )}

          {/* 危险操作：删除订单 */}
          {order.status !== 'completed' && (
            <Card
              style={{
                borderRadius: 8,
                borderColor: '#ff4d4f',
                background: '#fff5f5',
                marginTop: 16,
              }}
            >
              <Popconfirm
                title="确认删除该订单？"
                description="删除后无法恢复，请谨慎操作。"
                onConfirm={handleDelete}
                okText="确认删除"
                cancelText="取消"
                okButtonProps={{ danger: true }}
              >
                <Button
                  danger
                  block
                  icon={<DeleteOutlined />}
                  loading={deleting}
                >
                  删除订单
                </Button>
              </Popconfirm>
            </Card>
          )}
        </Col>
      </Row>

      {/* 发货弹窗 */}
      <Modal
        title="订单发货"
        open={shippingModalVisible}
        onCancel={() => setShippingModalVisible(false)}
        onOk={handleShipConfirm}
        okText="确认发货"
        cancelText="取消"
        width={480}
      >
        <Form form={shippingForm} layout="vertical" style={{ marginTop: 8 }}>
          <Form.Item
            name="trackingNumber"
            label="物流单号"
            rules={[{ required: true, message: '请输入物流单号' }]}
          >
            <Input placeholder="请输入物流单号" />
          </Form.Item>
          <Form.Item
            name="trackingCarrier"
            label="物流公司"
          >
            <Input placeholder="请输入物流公司名称（可选）" />
          </Form.Item>
        </Form>
      </Modal>

      {/* 退款弹窗 */}
      <Modal
        title="申请退款"
        open={refundModalVisible}
        onCancel={() => setRefundModalVisible(false)}
        onOk={handleRefundConfirm}
        okText="确认退款"
        cancelText="取消"
        width={480}
        confirmLoading={refunding}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">订单号：{order?.orderNo}</Text>
        </div>

        {(() => {
          const payment = getRefundModalPayment();
          if (!payment) return <Text type="danger">未找到支付记录</Text>;

          const paymentRefunds = refunds.filter(r => r.paymentId === payment.id);
          const alreadyRefunded = paymentRefunds.reduce((sum, r) => sum + (r.amount || 0), 0);
          const maxRefund = (payment.amount || 0) - alreadyRefunded;

          return (
            <Form layout="vertical">
              <Form.Item
                label={
                  <Space>
                    <span>退款金额（元）</span>
                    <Button
                      type="link"
                      size="small"
                      disabled={maxRefund <= 0}
                      onClick={() => setRefundAmount(maxRefund.toFixed(2))}
                      style={{ padding: 0, height: 'auto' }}
                    >
                      填全额 (${maxRefund.toFixed(2)})
                    </Button>
                  </Space>
                }
                required
                extra={`可退款金额：$${maxRefund.toFixed(2)}（已退 $${alreadyRefunded.toFixed(2)}）`}
              >
                <Input
                  type="number"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  placeholder="0.00"
                  prefix="$"
                  min="0"
                  step="0.01"
                  max={maxRefund}
                />
              </Form.Item>
              <Form.Item label="退款原因">
                <TextArea
                  rows={3}
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="请输入退款原因（可选）"
                />
              </Form.Item>
              <Form.Item
                label="管理员验证码"
                required
                extra="点击右侧按钮把 6 位验证码发送到管理员邮箱"
              >
                <Space.Compact style={{ width: '100%' }}>
                  <Input
                    value={refundOtp}
                    onChange={(e) => setRefundOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="6 位数字验证码"
                    maxLength={6}
                  />
                  <Button
                    onClick={handleSendOtp}
                    loading={otpSending}
                    disabled={otpCooldown > 0}
                  >
                    {otpCooldown > 0 ? `${otpCooldown}s` : '发送验证码'}
                  </Button>
                </Space.Compact>
              </Form.Item>
            </Form>
          );
        })()}
      </Modal>

      {/* 编辑商品弹窗 */}
      <Modal
        title="编辑订单商品"
        open={editItemsModalVisible}
        onCancel={() => setEditItemsModalVisible(false)}
        onOk={handleEditItemsConfirm}
        okText="确认修改"
        cancelText="取消"
        width={640}
        confirmLoading={editingItemsLoading}
      >
        <div style={{ marginBottom: 16 }}>
          <Text type="secondary">订单号：{order?.orderNo}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            提示：减少商品数量时，系统将自动计算退款金额并恢复库存
          </Text>
        </div>

        {/* 商品列表 */}
        <div style={{ maxHeight: 320, overflowY: 'auto' }}>
          {editingItems.map((item, index) => (
            <div
              key={item.orderItemId}
              style={{
                padding: '12px 16px',
                marginBottom: 8,
                background: '#fafafa',
                borderRadius: 8,
                border: item.newQuantity !== item.currentQuantity ? '1px solid #52c41a' : '1px solid transparent',
              }}
            >
              <Row gutter={16} align="middle">
                <Col span={8}>
                  <div>
                    <Text strong style={{ fontSize: 14 }}>{item.title || '-'}</Text>
                    {item.variantTitle && (
                      <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                        {item.variantTitle}
                      </Text>
                    )}
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        SKU: {item.sku || '-'} | 单价: ${Number(item.price || 0).toFixed(2)}
                      </Text>
                    </div>
                  </div>
                </Col>
                <Col span={4} style={{ textAlign: 'center' }}>
                  <Text type="secondary">当前数量</Text>
                  <div><Text strong>{item.currentQuantity}</Text></div>
                </Col>
                <Col span={8}>
                  <div>
                    <Text type="secondary" style={{ fontSize: 12 }}>新数量</Text>
                    <Input
                      type="number"
                      min={0}
                      max={999}
                      value={item.newQuantity}
                      onChange={(e) => handleEditItemChange(item.orderItemId, e.target.value)}
                      style={{ marginTop: 4 }}
                      status={item.newQuantity === 0 ? 'warning' : undefined}
                    />
                  </div>
                </Col>
                <Col span={4} style={{ textAlign: 'right' }}>
                  {item.newQuantity !== item.currentQuantity && (
                    <Space direction="vertical" size={0}>
                      {item.newQuantity < item.currentQuantity && (
                        <Text type="danger" style={{ fontSize: 12 }}>
                          减少 {item.currentQuantity - item.newQuantity} 件
                        </Text>
                      )}
                      {item.newQuantity > item.currentQuantity && (
                        <Text type="success" style={{ fontSize: 12 }}>
                          增加 {item.newQuantity - item.currentQuantity} 件
                        </Text>
                      )}
                    </Space>
                  )}
                </Col>
              </Row>
            </div>
          ))}
        </div>

        {/* 编辑原因 */}
        <Form layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item label="操作原因">
            <TextArea
              rows={2}
              value={editReason}
              onChange={(e) => setEditReason(e.target.value)}
              placeholder="请输入操作原因（可选，将记录在退款记录中）"
            />
          </Form.Item>
        </Form>

        {/* 变更摘要 */}
        {editingItems.some(item => item.newQuantity !== item.currentQuantity) && (
          <div
            style={{
              padding: 12,
              background: '#e6f7ff',
              borderRadius: 8,
              marginTop: 8,
            }}
          >
            <Text strong style={{ color: '#1890ff' }}>变更摘要：</Text>
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {editingItems
                .filter(item => item.newQuantity !== item.currentQuantity)
                .map(item => (
                  <li key={item.orderItemId} style={{ fontSize: 13 }}>
                    {item.title}
                    {item.variantTitle && ` - ${item.variantTitle}`}: {item.currentQuantity} → {item.newQuantity}
                    {item.newQuantity < item.currentQuantity && (
                      <Text type="danger" style={{ marginLeft: 8 }}>
                        （退款 ${(Number(item.price || 0) * (item.currentQuantity - item.newQuantity)).toFixed(2)}）
                      </Text>
                    )}
                  </li>
                ))}
            </ul>
          </div>
        )}
      </Modal>

      {/* 编辑收货地址弹窗 */}
      <Modal
        title="编辑收货地址"
        open={editAddressModalVisible}
        onCancel={() => setEditAddressModalVisible(false)}
        onOk={handleEditAddressConfirm}
        okText="保存"
        cancelText="取消"
        width={500}
        confirmLoading={addressSaving}
      >
        <Form
          form={addressForm}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="shippingFirstName"
                label="收货人名"
                rules={[{ required: true, message: '请填写收货人名' }]}
              >
                <Input placeholder="First Name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="shippingLastName"
                label="收货人姓"
                rules={[{ required: true, message: '请填写收货人姓' }]}
              >
                <Input placeholder="Last Name" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="shippingPhone"
            label="联系电话"
            rules={[{ required: true, message: '请填写联系电话' }]}
          >
            <Input placeholder="电话号码" />
          </Form.Item>
          <Form.Item
            name="shippingAddress1"
            label="街道地址1"
            rules={[{ required: true, message: '请填写街道地址' }]}
          >
            <Input placeholder="街道地址1" />
          </Form.Item>
          <Form.Item
            name="shippingAddress2"
            label="街道地址2（可选）"
          >
            <Input placeholder="街道地址2" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="shippingCity"
                label="城市"
                rules={[{ required: true, message: '请填写城市' }]}
              >
                <Input placeholder="城市" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="shippingProvince"
                label="省/州（可选）"
              >
                <Input placeholder="省/州" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="shippingPostalCode"
                label="邮编"
                rules={[{ required: true, message: '请填写邮编' }]}
              >
                <Input placeholder="邮编" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="shippingCountry"
                label="国家"
              >
                <Input placeholder="国家" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}